import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { employeeApi } from '@/services/employee/employee-api';
import { extractApiData } from '@/helper/ApiHelper';
import { IEmployeeViewModel, RequestDTO } from '@/types/employee';
import { pagerEmployeesFromIndexedDB } from '@/utils/employeeIndexedDBPager';
import debounce from 'lodash/debounce';
const PAGE_SIZE = 20;

/**
 * Cache module-level (dùng chung giữa mọi instance của hook — vd nhiều cell trong bảng đều mở
 * EmployeeAvatarSelect) để tránh gọi API trùng lặp khi cùng 1 phòng ban / query.
 * - Dedupe concurrent: nhiều instance cùng lúc gọi → dùng chung 1 Promise đang chạy.
 * - TTL ngắn (60s) để data gần realtime.
 */
type PagerCacheEntry = {
  options: { value: string; label: string }[];
  snapshots: Record<string, EmployeePagerSnapshot>;
  hasMore: boolean;
  at: number;
};
const pagerCache = new Map<string, PagerCacheEntry | Promise<PagerCacheEntry | null>>();
const PAGER_CACHE_TTL = 60_000;
/** Cache snapshot theo employeeSearchIDs (ensureSelectedLoaded) — TTL 30s. */
type SelectedPagerCacheEntry = { snaps: Record<string, EmployeePagerSnapshot>; at: number };
const selectedPagerCache = new Map<string, SelectedPagerCacheEntry>();
const SELECTED_CACHE_TTL = 30_000;

function cacheKey(params: {
  site: string;
  phanHe: string;
  departmentID: string;
  query: string;
  fromIndexedDB?: boolean;
}): string {
  return [
    params.fromIndexedDB ? 'idb' : 'api',
    params.site,
    params.phanHe,
    params.departmentID || 'all',
    (params.query || '').toLowerCase(),
  ].join('|');
}

async function requestEmployeePager(requestDTO: RequestDTO, fromIndexedDB?: boolean) {
  if (fromIndexedDB) {
    return pagerEmployeesFromIndexedDB(requestDTO);
  }
  return employeeApi.pager(requestDTO, true);
}

function readCache(key: string): PagerCacheEntry | null {
  const entry = pagerCache.get(key);
  if (!entry) return null;
  if (entry instanceof Promise) return null;
  if (Date.now() - entry.at > PAGER_CACHE_TTL) {
    pagerCache.delete(key);
    return null;
  }
  return entry;
}

type FetchPagerInput = {
  site: string;
  phanHe: string;
  pageNumber: number;
  pageSize: number;
  query: string;
  departmentID: string;
  positionID?: string;
  trangThaiLamViec?: string;
  ignoreID?: string;
  employeeSearchIDs?: string;
  layKiemNghiem: boolean;
  fromIndexedDB?: boolean;
};

/** Gọi pager có cache + dedupe concurrent (key = site|phanHe|dept|query). */
async function fetchPagerCached(
  input: FetchPagerInput
): Promise<PagerCacheEntry | null> {
  const key = cacheKey({
    site: input.site,
    phanHe: input.phanHe,
    departmentID: input.departmentID,
    query: input.query,
    fromIndexedDB: input.fromIndexedDB,
  });

  const cached = readCache(key);
  if (cached) {
    return cached;
  }

  // Dedupe concurrent: nếu đang có 1 Promise chạy cho cùng key → dùng chung.
  const pending = pagerCache.get(key);
  if (pending instanceof Promise) {
    return pending;
  }

  const promise = (async (): Promise<PagerCacheEntry | null> => {
    const response = await requestEmployeePager({
      site: input.site,
      pageNumber: input.pageNumber,
      pageSize: input.pageSize,
      query: input.query,
      departmentID: input.departmentID,
      positionID: input.positionID || '',
      trangThaiLamViec: input.trangThaiLamViec || 'Đang làm việc',
      phanHe: input.phanHe || '',
      ignoreID: input.ignoreID || '',
      employeeSearchIDs: input.employeeSearchIDs || undefined,
      layKiemNghiem: input.layKiemNghiem || false,
    }, input.fromIndexedDB);

    if (!response?.data) return null;
    const rawEmployees = extractApiData<IEmployeeViewModel>(response) || [];
    const employees = input.layKiemNghiem ? flattenEmployees(rawEmployees) : rawEmployees;
    const options = employees
      .map((emp: IEmployeeViewModel) => {
        const id = String(emp.id ?? emp.ID ?? '').trim();
        if (!id) return null;
        return { value: id, label: `${emp.tenNhanVien || emp.TenNhanVien || ''} ` };
      })
      .filter((opt): opt is { value: string; label: string } => opt != null);

    return {
      options,
      snapshots: snapshotsFromEmployees(employees),
      hasMore: rawEmployees.length >= input.pageSize,
      at: Date.now(),
    };
  })();

  pagerCache.set(key, promise);
  try {
    const entry = await promise;
    if (entry) pagerCache.set(key, entry);
    else pagerCache.delete(key);
    return entry;
  } catch (error) {
    pagerCache.delete(key);
    throw error;
  }
}

/** Dữ liệu hiển thị từ employee pager khi chưa có trong IndexedDB (vd phân hệ vanhoa) */
export interface EmployeePagerSnapshot {
    id: string;
    tenNhanVien: string;
    maNhanVien: string;
    hinh: string;
    tenBoPhan: string;
    tenChucVu: string;
}

const DEPT_SNAPSHOT_KEYS = [
    'tenBoPhan',
    'TenBoPhan',
    'boPhan',
    'BoPhan',
    'tenBP',
    'TenBP',
    'tenDonVi',
    'TenDonVi',
    'donVi',
    'DonVi',
    'tenPb',
    'TenPb',
    'tenPhongBan',
    'TenPhongBan',
    'departmentName',
    'DepartmentName',
];
const POS_SNAPSHOT_KEYS = [
    'tenChucVu',
    'TenChucVu',
    'chucVu',
    'ChucVu',
    'tenCV',
    'TenCV',
    'chucDanh',
    'ChucDanh',
    'positionName',
    'PositionName',
];

const pickFirstNonEmpty = (obj: Record<string, unknown>, keys: readonly string[]): string => {
    for (const key of keys) {
        const v = obj[key];
        if (v == null) continue;
        const s = String(v).trim();
        if (s) return s;
    }
    return '';
};

const employeeToSnapshot = (emp: IEmployeeViewModel): EmployeePagerSnapshot | null => {
    const id = String(emp.id ?? emp.ID ?? '').trim();
    if (!id) return null;
    const raw = emp as unknown as Record<string, unknown>;
    const str = (v: unknown) => (v == null ? '' : String(v).trim());
    return {
        id,
        tenNhanVien: str(emp.tenNhanVien ?? emp.TenNhanVien),
        maNhanVien: str(emp.maNhanVien ?? raw.MaNhanVien),
        hinh: str(emp.hinh),
        tenBoPhan: pickFirstNonEmpty(raw, DEPT_SNAPSHOT_KEYS),
        tenChucVu: pickFirstNonEmpty(raw, POS_SNAPSHOT_KEYS),
    };
};

const snapshotsFromEmployees = (employees: IEmployeeViewModel[]): Record<string, EmployeePagerSnapshot> => {
    const out: Record<string, EmployeePagerSnapshot> = {};
    for (const emp of employees) {
        const s = employeeToSnapshot(emp);
        if (s) out[s.id] = s;
    }
    return out;
};

// Flatten employees tree (include childs recursively)
const flattenEmployees = (employees: IEmployeeViewModel[] = []): IEmployeeViewModel[] => {
    const result: IEmployeeViewModel[] = [];

    const walk = (items: IEmployeeViewModel[]) => {
        items.forEach(emp => {
            result.push(emp);
            if (Array.isArray(emp.childs) && emp.childs.length > 0) {
                walk(emp.childs);
            }
        });
    };

    walk(employees);
    return result;
};
interface UseTreeDepartmentEmployeeProps {
    tenantID: string;
    selectedDepartmentId?: string;
    initialEmployees?: { value: string; label: string }[];
    nhanViens?: string; // String format: ",id,id,id,"
    selectedEmployeeIds?: string[]; // Fallback for selected employee IDs
    enabled?: boolean; // Skip API calls when false (e.g., modal closed)
    layKiemNghiem?: boolean;
    /** Phân hệ khi gọi employeeApi.pager (vd: bsc, vanhoa) */
    phanHe?: string;
    /** Chỉ AP V2 table: lấy NV từ IndexedDB, không gọi pager-sdk. */
    fromIndexedDB?: boolean;
}

interface UseTreeDepartmentEmployeeReturn {
    employeeOptions: { value: string; label: string }[];
    /** Đang tải dữ liệu (dùng cho Select loading) */
    loading: boolean;
    loadingEmployees: boolean;
    selectedEmployee: { value: string; label: string } | undefined;
    setSelectedEmployee: (employee: { value: string; label: string } | undefined) => void;
    loadEmployeesByDepartment: (departmentId: string) => Promise<void>;
    loadAllEmployees: (nhanViens?: string, query?: string) => Promise<void>;
    loadMoreEmployees: (searchQuery?: string) => Promise<void>;
    hasMoreEmployees: boolean;
    selectedEmployeeIds: string[]; // Return selected employee IDs from nhanViens
    searchEmployees: (query: string) => void;
    /** Bản ghi NV theo id từ lần gọi pager gần nhất — dùng render option khi IndexedDB thiếu */
    employeeSnapshots: Record<string, EmployeePagerSnapshot>;
    /** Chỉ tải các NV đã chọn (không thay list hiển thị) — merge snapshot để hiện ngay tên/avatar khi chọn xong. */
    ensureSelectedLoaded: (ids: string[]) => Promise<void>;
}

export const useTreeDepartmentEmployee = ({
    tenantID,
    selectedDepartmentId,
    initialEmployees = [],
    nhanViens,
    selectedEmployeeIds: fallbackEmployeeIds = [],
    enabled = true,
    layKiemNghiem = false,
    phanHe: phanHeProp = 'bsc',
    fromIndexedDB = false,
}: UseTreeDepartmentEmployeeProps): UseTreeDepartmentEmployeeReturn => {
    const [employeeOptions, setEmployeeOptions] = useState<{ value: string; label: string }[]>(initialEmployees);
    const [employeeSnapshots, setEmployeeSnapshots] = useState<Record<string, EmployeePagerSnapshot>>({});
    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<{ value: string; label: string } | undefined>(undefined);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreEmployees, setHasMoreEmployees] = useState(true);
    const [currentDepartmentId, setCurrentDepartmentId] = useState<string | undefined>(undefined);
    const phanHe = phanHeProp || 'bsc';
    // Track latest search query for infinite scrolling consistency
    const currentQueryRef = useRef<string>('');
    // Mirror latest employeeOptions for loadMore merge (avoid stale closure)
    const employeeOptionsRef = useRef<{ value: string; label: string }[]>([]);
    // Prevent duplicate initial loads (e.g., StrictMode or multiple triggers)
    const hasLoadedInitialRef = useRef<boolean>(false);
    /** Bump khi search/loadAll mới — bỏ kết quả in-flight cũ. */
    const fetchGenRef = useRef(0);
    // state (under currentPage/hasMoreEmployees)
    const [currentQuery, setCurrentQuery] = useState<string>('');

    // Parse nhanViens string to get selected employee IDs
    const getSelectedEmployeeIds = useCallback(() => {
        if (nhanViens && nhanViens.trim() !== '') {
            const ids = nhanViens.split(',').filter(id => id.trim() !== '');
            return ids;
        }
        // Fallback to selectedEmployeeIds if nhanViens is empty
        return fallbackEmployeeIds;
    }, [fallbackEmployeeIds]);

    // Load employees by department ID using pagerselect
    const loadEmployeesByDepartment = useCallback(async (departmentId: string) => {
        if (!tenantID || !departmentId || !enabled) return;

        setLoadingEmployees(true); // loading cho API pager
        setCurrentPage(1);
        setCurrentDepartmentId(departmentId);

        try {
            const selectedEmployeeIds = getSelectedEmployeeIds().join(',');
            const entry = await fetchPagerCached({
                site: tenantID,
                phanHe: phanHe || '',
                pageNumber: 1,
                pageSize: PAGE_SIZE,
                query: '',
                departmentID: departmentId,
                employeeSearchIDs: selectedEmployeeIds || undefined,
                layKiemNghiem: layKiemNghiem || false,
                fromIndexedDB,
            });

            if (entry) {
                setEmployeeOptions(entry.options);
                employeeOptionsRef.current = entry.options;
                setEmployeeSnapshots(entry.snapshots);
                setHasMoreEmployees(entry.hasMore);
                // Clear selected employee when department changes
                setSelectedEmployee(undefined);
            }
        } catch (error) {
            console.error('Failed to load employees by department:', error);
            setEmployeeOptions([]);
            setEmployeeSnapshots({});
            setHasMoreEmployees(false);
        } finally {
            setLoadingEmployees(false);
        }
    }, [tenantID, enabled, layKiemNghiem, phanHe, fromIndexedDB, getSelectedEmployeeIds]);

    // Load all employees (fallback) using pagerselect
    const loadAllEmployees = useCallback(async (nhanViensParam?: string, query?: string) => {
        if (!tenantID || !enabled) return;

        const effectiveQuery =
            query !== undefined ? query : (currentQueryRef.current || '');
        const gen = ++fetchGenRef.current;
        setLoadingEmployees(true); // loading cho API pagerselect
        setCurrentPage(1);
        setCurrentDepartmentId(undefined);
        setCurrentQuery(effectiveQuery);
        currentQueryRef.current = effectiveQuery;

        try {
            const selectedEmployeeIds = nhanViensParam ?? getSelectedEmployeeIds().join(',');
            const entry = await fetchPagerCached({
                site: tenantID,
                phanHe: phanHe || '',
                pageNumber: 1,
                pageSize: PAGE_SIZE,
                query: effectiveQuery,
                departmentID: '',
                employeeSearchIDs: selectedEmployeeIds || undefined,
                layKiemNghiem: layKiemNghiem || false,
                fromIndexedDB,
            });

            if (gen !== fetchGenRef.current) return;
            if (entry) {
                setEmployeeOptions(entry.options);
                employeeOptionsRef.current = entry.options;
                setEmployeeSnapshots(entry.snapshots);
                setHasMoreEmployees(entry.hasMore);
                hasLoadedInitialRef.current = true;
            }
        } catch (error) {
            if (gen !== fetchGenRef.current) return;
            console.error('Failed to load all employees:', error);
            setEmployeeOptions([]);
            setEmployeeSnapshots({});
            setHasMoreEmployees(false);
        } finally {
            if (gen === fetchGenRef.current) setLoadingEmployees(false);
        }
    }, [tenantID, enabled, getSelectedEmployeeIds, layKiemNghiem, phanHe, fromIndexedDB]);

    // Load more employees (for lazy loading)
    const loadMoreEmployees = useCallback(async (searchQuery?: string) => {
        if (!tenantID || !hasMoreEmployees || loadingEmployees || !enabled) return;
        const gen = fetchGenRef.current;
        setLoadingEmployees(true); // loading cho API pagerselect (load more)
        const nextPage = currentPage + 1;
        const effectiveQuery = (searchQuery !== undefined ? searchQuery : currentQueryRef.current) || '';

        try {
            const response = await requestEmployeePager({
                site: tenantID,
                pageNumber: nextPage,
                pageSize: PAGE_SIZE,
                query: effectiveQuery,
                departmentID: currentDepartmentId || '',
                positionID: '',
                trangThaiLamViec: 'Đang làm việc',
                phanHe: phanHe || '',
                layKiemNghiem: layKiemNghiem || false,
            }, fromIndexedDB);

            if (gen !== fetchGenRef.current) return;
            if (response.data) {
                const rawEmployees = extractApiData<IEmployeeViewModel>(response) || [];
                const employees = layKiemNghiem ? flattenEmployees(rawEmployees) : rawEmployees;
                const incomingOptions = employees
                    .map((emp: IEmployeeViewModel) => {
                        const id = String(emp.id ?? emp.ID ?? '').trim();
                        if (!id) return null;
                        return {
                            value: id,
                            label: `${emp.tenNhanVien || emp.TenNhanVien || ''} `,
                        };
                    })
                    .filter((opt): opt is { value: string; label: string } => opt != null);

                const pageSnaps = snapshotsFromEmployees(employees);
                setEmployeeSnapshots((prev) => ({ ...prev, ...pageSnaps }));

                // Tính trước outside updater — tránh side-effect trong setState (double-invoke ở StrictMode).
                const existingIds = new Set(employeeOptionsRef.current.map(o => o.value));
                const uniqueNew = incomingOptions.filter(o => !existingIds.has(o.value));
                const reachedEnd = rawEmployees.length < PAGE_SIZE || uniqueNew.length === 0;

                setEmployeeOptions(prev => {
                    const prevIds = new Set(prev.map(o => o.value));
                    const newRows = incomingOptions.filter(o => !prevIds.has(o.value));
                    return newRows.length > 0 ? [...prev, ...newRows] : prev;
                });
                employeeOptionsRef.current = [...employeeOptionsRef.current, ...uniqueNew];
                setHasMoreEmployees(!reachedEnd);
                if (uniqueNew.length > 0) {
                    setCurrentPage(nextPage);
                }
            } else {
                setHasMoreEmployees(false);
            }
        } catch (error) {
            if (gen !== fetchGenRef.current) return;
            console.error('Failed to load more employees:', error);
            setHasMoreEmployees(false);
        } finally {
            if (gen === fetchGenRef.current) setLoadingEmployees(false);
        }
    }, [tenantID, enabled, layKiemNghiem, currentPage, hasMoreEmployees, loadingEmployees, currentDepartmentId, phanHe, fromIndexedDB]);

    // Internal search function (not debounced)
    const searchEmployeesInternal = useCallback(async (query: string) => {
        if (!tenantID || !enabled) return;

        const gen = ++fetchGenRef.current;
        setLoadingEmployees(true); // loading cho API pagerselect (search)
        setCurrentPage(1);
        setCurrentQuery(query || '');
        currentQueryRef.current = query || '';
        try {
            const selectedEmployeeIds = getSelectedEmployeeIds().join(',');
            const entry = await fetchPagerCached({
                site: tenantID,
                phanHe: phanHe || '',
                pageNumber: 1,
                pageSize: PAGE_SIZE,
                query: query || '',
                departmentID: currentDepartmentId || '',
                employeeSearchIDs: selectedEmployeeIds,
                layKiemNghiem: layKiemNghiem || false,
                fromIndexedDB,
            });
            if (gen !== fetchGenRef.current) return;
            if (entry) {
                setEmployeeOptions(entry.options);
                employeeOptionsRef.current = entry.options;
                setEmployeeSnapshots(entry.snapshots);
                setHasMoreEmployees(entry.hasMore);
            }
        } catch {
            if (gen !== fetchGenRef.current) return;
            setEmployeeOptions([]);
            employeeOptionsRef.current = [];
            setEmployeeSnapshots({});
            setHasMoreEmployees(false);
        } finally {
            if (gen === fetchGenRef.current) setLoadingEmployees(false);
        }
    }, [tenantID, enabled, currentDepartmentId, getSelectedEmployeeIds, layKiemNghiem, phanHe, fromIndexedDB]);

    // Debounced search function
    const searchEmployees = useMemo(
        () => debounce(async (query: string) => {
            await searchEmployeesInternal(query);
        }, 500),
        [searchEmployeesInternal]
    );

    // Chỉ tải các NV đã chọn — merge snapshot (không thay list hiển thị).
    const ensureSelectedLoaded = useCallback(async (ids: string[]) => {
        const realIds = (ids || []).filter((id) => String(id).trim());
        if (!tenantID || !enabled || realIds.length === 0) return;
        setLoadingEmployees(true);
        try {
            // Cache module-level theo employeeSearchIDs — nhiều cell cùng chọn NV → 1 request.
            const sigKey = `${fromIndexedDB ? 'idb' : 'api'}|${tenantID}|${phanHe}|${realIds.join(',')}`;
            const rawCachedSel = selectedPagerCache.get(sigKey);
            if (rawCachedSel && Date.now() - rawCachedSel.at < SELECTED_CACHE_TTL) {
                setEmployeeSnapshots((prev) => ({ ...prev, ...rawCachedSel.snaps }));
                return;
            }
            const response = await requestEmployeePager({
                site: tenantID,
                pageNumber: 1,
                pageSize: Math.max(PAGE_SIZE, realIds.length),
                query: '',
                departmentID: currentDepartmentId || '',
                positionID: '',
                trangThaiLamViec: 'Đang làm việc',
                phanHe: phanHe || '',
                employeeSearchIDs: realIds.join(','),
                layKiemNghiem: layKiemNghiem || false,
            }, fromIndexedDB);
            if (response?.data) {
                const rawEmployees = extractApiData<IEmployeeViewModel>(response) || [];
                const employees = layKiemNghiem ? flattenEmployees(rawEmployees) : rawEmployees;
                const snaps = snapshotsFromEmployees(employees);
                selectedPagerCache.set(sigKey, { snaps, at: Date.now() });
                setEmployeeSnapshots((prev) => ({ ...prev, ...snaps }));
            }
        } catch {
            // im lặng — chỉ là snapshot bổ sung
        } finally {
            setLoadingEmployees(false);
        }
    }, [tenantID, enabled, currentDepartmentId, layKiemNghiem, phanHe, fromIndexedDB]);

    // Auto-load employees when department changes
    useEffect(() => {
        if (!enabled) return;
        if (selectedDepartmentId) {
            // Only react when department changes, not when employee selection changes
            loadEmployeesByDepartment(selectedDepartmentId);
        } else {
            // Intentionally do nothing on no-department to avoid API calls on employee select
            // The caller can explicitly call loadAllEmployees when opening modal if needed
        }
    }, [enabled, selectedDepartmentId]);

    return {
        employeeOptions,
        loading: loadingEmployees,
        loadingEmployees,
        selectedEmployee,
        setSelectedEmployee,
        loadEmployeesByDepartment,
        loadAllEmployees,
        loadMoreEmployees,
        hasMoreEmployees,
        searchEmployees,
        selectedEmployeeIds: getSelectedEmployeeIds(),
        employeeSnapshots,
        ensureSelectedLoaded,
    };
};
